import { create } from 'zustand';
import { Timestamp } from '@firebase/firestore';
import { agentsApi, type ClientStructure } from '../api/agents';
import { reportsApi } from '../api/reports';
import type { InsuranceChecklist } from '../types';
import type { GenerateReportResult } from '../types/api';
import { withLoading } from './utils/withLoading';

// One bounded fetch (checklists + claims `in` query + structure gets — the
// cost-doc-blessed Q13 shape) shared by the agent hub, the clients screen,
// and the report sheet. The staleness gate keeps hub → clients → back at a
// single fetch per session instead of one per navigation.
const STALE_AFTER_MS = 2 * 60 * 1000;

interface AgentClientsState {
  clients: ClientStructure[];
  checklistsById: Record<string, InsuranceChecklist>;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  fetchClients: (insurerName: string, opts?: { force?: boolean }) => Promise<void>;
  /** Optimistic local patch after a successful report send. */
  markReportSent: (structureId: string, recipientsCount: number) => void;
  /**
   * Generate and send a readiness report for a structure. Updates
   * the client list optimistically on success. Rethrows so the sheet
   * can surface the error (including the daily rate-limit message).
   */
  sendReport: (structureId: string) => Promise<GenerateReportResult>;
  clearError: () => void;
  reset: () => void;
}

export const useAgentClientsStore = create<AgentClientsState>((set, get) => ({
  clients: [],
  checklistsById: {},
  isLoading: false,
  error: null,
  lastFetchedAt: null,

  fetchClients: async (insurerName, opts) => {
    const { lastFetchedAt, isLoading } = get();
    if (isLoading) return;
    if (!opts?.force && lastFetchedAt && Date.now() - lastFetchedAt < STALE_AFTER_MS) return;

    await withLoading(set, 'isLoading', async () => {
      const { clients, checklists } =
        await agentsApi.listClientStructuresWithChecklists(insurerName);
      set({
        clients,
        checklistsById: Object.fromEntries(checklists.map((c) => [c.id, c])),
        lastFetchedAt: Date.now(),
      });
    });
  },

  markReportSent: (structureId, recipientsCount) =>
    set((state) => ({
      clients: state.clients.map((c) =>
        c.structure.id === structureId
          ? {
              ...c,
              claim: {
                ...c.claim,
                lastReport: { at: Timestamp.now(), byRole: 'agent', recipientsCount },
                reportCount: (c.claim.reportCount ?? 0) + 1,
              },
            }
          : c
      ),
    })),

  sendReport: async (structureId: string) => {
    const result = await reportsApi.generateStructureReport(structureId);
    get().markReportSent(structureId, result.recipients.length);
    return result;
  },

  clearError: () => set({ error: null }),
  reset: () =>
    set({ clients: [], checklistsById: {}, isLoading: false, error: null, lastFetchedAt: null }),
}));
